import { useQuery } from "@tanstack/react-query";
import { Icon } from "../components/ui/Icon.js";
import { apiFetch } from "../lib/auth.js";

interface ModelInfo {
  activeVersion: string;
  deployedAt: number | null;
  trainingMode: "vps-realtime" | "local-gpu" | "disabled";
  metrics: {
    directionalAccuracy: number;
    expectedCalibrationError: number;
    profitFactor: number;
    tradesTrainedOn: number;
  };
  nextRetrainMs: number | null;
  lastRetrain: {
    host: string;
    durationMs: number;
    completedAt: number;
  } | null;
  pipeline: {
    step: number;
    label: string;
    state: "done" | "active" | "pending";
  }[];
  featureImportance: { name: string; weight: number }[];
  calibration: { bucket: number; actual: number }[];
}

const FALLBACK: ModelInfo = {
  activeVersion: "v0.0.0-stub",
  deployedAt: null,
  trainingMode: "local-gpu",
  metrics: {
    directionalAccuracy: 0,
    expectedCalibrationError: 0,
    profitFactor: 0,
    tradesTrainedOn: 0,
  },
  nextRetrainMs: null,
  lastRetrain: null,
  pipeline: [
    { step: 1, label: "Closed trade", state: "pending" },
    { step: 2, label: "Feature extraction", state: "pending" },
    { step: 3, label: "Training buffer", state: "pending" },
    { step: 4, label: "Model retrain", state: "pending" },
    { step: 5, label: "A/B vs champion", state: "pending" },
    { step: 6, label: "Deploy if better", state: "pending" },
  ],
  featureImportance: [],
  calibration: [],
};

async function fetchModel(): Promise<ModelInfo> {
  const res = await apiFetch("/v1/model");
  if (!res.ok) return FALLBACK;
  return (await res.json()) as ModelInfo;
}

function modeLabel(mode: ModelInfo["trainingMode"]): { text: string; tone: string } {
  switch (mode) {
    case "vps-realtime":
      return { text: "VPS REALTIME", tone: "var(--primary)" };
    case "local-gpu":
      return { text: "LOCAL GPU", tone: "var(--ai)" };
    default:
      return { text: "DISABLED", tone: "var(--text-muted)" };
  }
}

function fmtDuration(ms: number | null): string {
  if (ms === null || ms <= 0) return "—";
  const minutes = Math.floor(ms / 60_000);
  const hours = Math.floor(minutes / 60);
  const remMin = minutes - hours * 60;
  if (hours > 0) return `${hours}h ${remMin}m`;
  return `${minutes}m`;
}

export function TrainingCenterPage() {
  const q = useQuery({
    queryKey: ["model"],
    queryFn: fetchModel,
    refetchInterval: 30_000,
  });
  const data = q.data ?? FALLBACK;
  const mode = modeLabel(data.trainingMode);
  const maxImportance = Math.max(...data.featureImportance.map((f) => f.weight), 1);

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", overflowY: "auto" }}>
      <div
        style={{
          padding: "12px 18px",
          borderBottom: "1px solid var(--border)",
          display: "flex",
          alignItems: "center",
          gap: 12,
        }}
      >
        <Icon name="cpu" size={18} className="ai-text" />
        <div>
          <div style={{ fontSize: 14, fontWeight: 600 }}>AI Training Center</div>
          <div className="muted" style={{ fontSize: 11 }}>
            Self-learning GBT ensemble + isotonic calibration · spot BUY classifier
          </div>
        </div>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
          <span
            className="se-pill"
            style={{
              background: "rgba(168, 85, 247, 0.1)",
              color: mode.tone,
              borderColor: mode.tone,
            }}
          >
            <Icon name="cpu" size={10} /> Mode · {mode.text}
          </span>
          <button type="button" className="se-btn">
            <Icon name="play" size={13} /> Trigger retrain
          </button>
        </div>
      </div>

      <div style={{ padding: 18 }}>
        <div className="se-card" style={{ padding: 0, marginBottom: 14 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)" }}>
            {[
              {
                l: "Active model",
                v: data.activeVersion,
                sub: data.deployedAt
                  ? `Deployed ${new Date(data.deployedAt).toLocaleString()}`
                  : "Not yet deployed",
              },
              {
                l: "Directional accuracy",
                v: `${(data.metrics.directionalAccuracy * 100).toFixed(1)}%`,
                sub: "30d rolling",
                up: data.metrics.directionalAccuracy > 0.5,
              },
              {
                l: "ECE",
                v: data.metrics.expectedCalibrationError.toFixed(3),
                sub: "lower is better",
                ai: true,
              },
              {
                l: "Profit factor (paper)",
                v: data.metrics.profitFactor.toFixed(2),
                sub: "gross G / gross L",
              },
              {
                l: "Trades trained on",
                v: data.metrics.tradesTrainedOn.toLocaleString(),
                sub: "time-decay weighted",
              },
              {
                l: "Next retrain",
                v: fmtDuration(data.nextRetrainMs),
                sub: data.trainingMode === "vps-realtime" ? "auto · every 6h" : "manual via local CLI",
                ai: true,
              },
            ].map((c, i) => (
              <div
                key={c.l}
                style={{
                  padding: 14,
                  borderRight: i < 5 ? "1px solid var(--border)" : "none",
                  borderLeft: i === 0 ? "2px solid var(--ai)" : "none",
                }}
              >
                <div
                  className="muted"
                  style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.06em" }}
                >
                  {c.l}
                </div>
                <div
                  className="mono tnum"
                  style={{
                    fontSize: 18,
                    fontWeight: 600,
                    marginTop: 3,
                    color: c.up ? "var(--buy)" : c.ai ? "var(--ai)" : "var(--text)",
                  }}
                >
                  {c.v}
                </div>
                <div className="muted" style={{ fontSize: 10, marginTop: 2 }}>
                  {c.sub}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="se-card" style={{ marginBottom: 14 }}>
          <div className="se-card-head">
            <div>Self-learning pipeline</div>
            <span className="se-pill se-pill-ai">
              <Icon name="sparkle" size={10} /> Mode · {mode.text}
            </span>
          </div>
          <div style={{ padding: "24px 18px" }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 0 }}>
              {data.pipeline.map((step, i) => (
                <div
                  key={step.step}
                  style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}
                >
                  <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
                    <div style={{ flex: 1, height: 1, background: i === 0 ? "transparent" : "var(--border)" }} />
                    <div
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: 10,
                        background:
                          step.state === "active"
                            ? "rgba(168, 85, 247, 0.15)"
                            : step.state === "done"
                              ? "rgba(34, 197, 94, 0.12)"
                              : "var(--surface-2)",
                        border:
                          step.state === "active"
                            ? "1px solid var(--ai)"
                            : step.state === "done"
                              ? "1px solid rgba(34, 197, 94, 0.4)"
                              : "1px solid var(--border-soft)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontFamily: "JetBrains Mono",
                        fontSize: 12,
                        fontWeight: 600,
                        color:
                          step.state === "active"
                            ? "var(--ai)"
                            : step.state === "done"
                              ? "var(--buy)"
                              : "var(--text-faint)",
                        animation: step.state === "active" ? "ai-pulse 2s infinite" : "none",
                      }}
                    >
                      {step.state === "done" ? <Icon name="check" size={16} /> : step.step}
                    </div>
                    <div
                      style={{
                        flex: 1,
                        height: 1,
                        background:
                          i === data.pipeline.length - 1
                            ? "transparent"
                            : step.state === "done"
                              ? "rgba(34, 197, 94, 0.3)"
                              : "var(--border)",
                      }}
                    />
                  </div>
                  <div style={{ fontSize: 11, fontWeight: 500, textAlign: "center" }}>
                    {step.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div className="se-card">
            <div className="se-card-head">
              <div>Feature importance</div>
              <span className="se-card-sub">top 12</span>
            </div>
            <div style={{ padding: 14 }}>
              {data.featureImportance.length === 0 ? (
                <div className="muted" style={{ fontSize: 11 }}>
                  No model trained yet. Run the local trainer or wait for the
                  scheduled VPS retrain.
                </div>
              ) : (
                data.featureImportance.map((f) => (
                  <div
                    key={f.name}
                    style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 0", fontSize: 11 }}
                  >
                    <span style={{ width: 180 }}>{f.name}</span>
                    <div className="se-progress" style={{ flex: 1 }}>
                      <div
                        className="se-progress-bar ai"
                        style={{ width: `${(f.weight / maxImportance) * 100}%` }}
                      />
                    </div>
                    <span className="mono tnum" style={{ width: 40, textAlign: "right" }}>
                      {f.weight.toFixed(1)}%
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="se-card">
            <div className="se-card-head">
              <div>Calibration analysis</div>
              <span className="se-card-sub">
                ECE {data.metrics.expectedCalibrationError.toFixed(3)}
              </span>
            </div>
            <div style={{ padding: 14 }}>
              {data.calibration.length === 0 ? (
                <div className="muted" style={{ fontSize: 11 }}>
                  Calibration data published after the first walk-forward
                  validation completes.
                </div>
              ) : (
                <svg width="100%" height="180" viewBox="0 0 320 180">
                  <line x1="20" y1="160" x2="300" y2="20" stroke="var(--border-soft)" strokeDasharray="3 3" />
                  {data.calibration.map((b, i) => {
                    const x = 20 + (b.bucket / 100) * 280 - 12;
                    const h = b.actual * 140;
                    return (
                      <rect
                        key={i}
                        x={x}
                        y={160 - h}
                        width={24}
                        height={h}
                        fill="rgba(168, 85, 247, 0.5)"
                        stroke="var(--ai)"
                        strokeWidth="1"
                      />
                    );
                  })}
                </svg>
              )}
            </div>
          </div>
        </div>

        <div className="se-card" style={{ marginTop: 14, padding: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>
            Local-GPU training quick start
          </div>
          <pre
            style={{
              margin: 0,
              padding: 12,
              background: "var(--bg)",
              border: "1px solid var(--border)",
              borderRadius: 6,
              fontFamily: "var(--font-mono)",
              fontSize: 11,
              color: "var(--text)",
              overflowX: "auto",
            }}
          >
{`# Requires: NVIDIA driver + CUDA 12 + Python 3.11
git clone <repo> && cd CryptoTrading/tools/local-trainer
pip install -e .
cp .env.example .env  # set VPS_API_BASE + VPS_API_TOKEN

local-trainer gpu-check         # confirms CUDA device
local-trainer train --since 30d # pulls features, trains on GPU
local-trainer upload             # POSTs ONNX to VPS model registry
local-trainer activate <id>      # makes the new model live`}
          </pre>
        </div>
      </div>
    </div>
  );
}
